import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { allArticles, type Article } from '@/mocks/articles';

function getSlugFromPath(pathname: string): string {
  const cleaned = pathname.replace(/^\/+|\/+$/g, '');
  return cleaned || '';
}

function renderArticleHTML(article: Article): string {
  let html = '';
  article.sections.forEach((section) => {
    if (section.type === 'heading') {
      const tag = section.headingLevel || 'h2';
      html += `<${tag} class="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4 mt-10 first:mt-0">${section.heading}</${tag}>`;
    } else if (section.type === 'paragraph') {
      section.paragraphs?.forEach((p) => {
        html += `<p class="mb-4">${p}</p>`;
      });
    } else if (section.type === 'list') {
      const tag = section.listOrdered ? 'ol' : 'ul';
      const listClass = section.listOrdered ? 'list-decimal' : 'list-disc';
      html += `<${tag} class="${listClass} pl-5 mb-4 space-y-2">`;
      section.listItems?.forEach((item) => {
        html += `<li class="text-foreground-700">${item}</li>`;
      });
      html += `</${tag}>`;
    } else if (section.type === 'table') {
      const td = section.tableData;
      if (td) {
        html += '<div class="bg-background-100 rounded-xl p-5 md:p-6 mb-6 border border-background-200/60 overflow-x-auto"><table class="w-full text-sm"><thead><tr class="border-b border-background-300/60">';
        td.headers.forEach((h, i) => {
          const align = i === 0 ? 'text-left' : 'text-center';
          html += `<th class="${align} py-2 pr-4 font-label font-semibold text-foreground-700 text-xs uppercase tracking-wider whitespace-nowrap">${h}</th>`;
        });
        html += '</tr></thead><tbody>';
        td.rows.forEach((row, ri) => {
          html += `<tr class="border-b border-background-200/40 ${ri === td.rows.length - 1 ? '' : ''}">`;
          row.forEach((cell, ci) => {
            const cellAlign = ci === 0 ? 'text-left font-medium text-foreground-700' : 'text-center text-foreground-600';
            html += `<td class="py-3 pr-4 ${cellAlign} text-xs">${cell}</td>`;
          });
          html += '</tr>';
        });
        html += '</tbody></table></div>';
      }
    } else if (section.type === 'cta') {
      html += '<div class="mt-12 p-6 bg-background-100 rounded-xl border border-background-200/60"><div class="flex items-center gap-3 mb-3"><span class="w-5 h-5 flex items-center justify-center text-primary-500"><i class="ri-information-line"></i></span><p class="text-xs text-foreground-500 font-medium">Ready to start shopping?</p></div><p class="text-sm text-foreground-600">Browse the latest categories and trusted sellers on the <a href="/" class="text-primary-500 hover:text-primary-600 underline font-medium">AllChinaBuy Spreadsheet homepage</a>.</p></div>';
    }
  });
  return html;
}

export default function ArticlePage() {
  const location = useLocation();
  const slug = getSlugFromPath(location.pathname);
  const article = allArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <section className="pt-32 pb-20 px-4 md:px-6 bg-background-50 min-h-[60vh] flex items-center justify-center">
            <div className="text-center max-w-md">
              <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">Article Not Found</h1>
              <p className="text-sm text-foreground-500 mb-6">The article you are looking for does not exist or may have been moved.</p>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Back to Homepage
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <article>
          {/* Article Hero */}
          <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-center bg-background-100 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/50 via-background-50/30 to-background-50/95"></div>

            <div className="relative z-10 w-full px-4 md:px-6 pt-20 md:pt-24">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
                  <Link to="/" className="hover:text-primary-500 transition-colors">
                    Home
                  </Link>
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </span>
                  <span className="text-foreground-800 font-medium">{article.category}</span>
                </div>

                <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6">
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line"></i>
                    </span>
                    Last Updated: June 25, 2026
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line"></i>
                    </span>
                    Reading Time: {article.readTime}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line"></i>
                    </span>
                    By Editorial Team
                  </span>
                </div>

                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-primary-300/60 text-primary-600 rounded-full hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-xs"></i>
                  </span>
                  Back to Home
                </Link>
              </div>
            </div>
          </section>

          {/* Article Body */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div
                className="prose-custom text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: renderArticleHTML(article) }}
              />

              <div className="mt-12 p-6 bg-background-100 rounded-xl border border-background-200/60">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                    <i className="ri-information-line"></i>
                  </span>
                  <p className="text-xs text-foreground-500 font-medium">
                    Last Updated: June 25, 2026 &middot; Reading Time: {article.readTime}
                  </p>
                </div>
                <p className="text-sm text-foreground-600">
                  This guide is regularly reviewed and updated to reflect the latest changes in the AllChinaBuy Spreadsheet. For the most current seller ratings and product links, visit the{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 underline font-medium">
                    AllChinaBuy Spreadsheet homepage
                  </Link>
                  .
                </p>
              </div>
            </div>
          </section>

          {/* Related Categories */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">
                Browse Popular Categories
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories.slice(0, 8).map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-background-50 border border-background-200/60 rounded-full text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </section>

          {/* Article Navigation */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">
                More AllChinaBuy Spreadsheet Guides
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {allArticles
                  .filter((a) => a.slug !== slug)
                  .slice(0, 6)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/${a.slug}`}
                      className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200/60 hover:border-primary-300 transition-colors cursor-pointer group"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-50 text-primary-500 flex-shrink-0 mt-0.5 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                        <i className="ri-article-line text-sm"></i>
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
                          {a.title}
                        </p>
                        <p className="text-xs text-foreground-400 mt-1">{a.readTime} read</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </section>

          {/* Article CTA */}
          <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                Ready to Start Shopping?
              </h2>
              <p className="text-sm text-foreground-500 mb-8 max-w-md mx-auto">
                Browse the latest <strong>AllChinaBuy Spreadsheet</strong> categories and find trusted sellers with verified products today.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-7 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm inline-flex items-center gap-2"
                >
                  Explore Spreadsheet
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </a>
                <Link
                  to="/"
                  className="px-7 py-3 border border-foreground-300 text-foreground-700 font-medium rounded-full hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer text-sm inline-flex items-center gap-2"
                >
                  Back to Homepage
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-home-line text-xs"></i>
                  </span>
                </Link>
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            '@id': `https://allchinabuyspreadsheet.bar/${article.slug}/#article`,
            headline: article.title,
            description: article.description,
            image: article.imageUrl,
            datePublished: '2026-06-25',
            dateModified: '2026-06-25',
            author: {
              '@type': 'Organization',
              '@id': 'https://allchinabuyspreadsheet.bar/#organization',
              name: 'AllChinaBuySpreadsheet',
            },
            publisher: {
              '@type': 'Organization',
              '@id': 'https://allchinabuyspreadsheet.bar/#organization',
              name: 'AllChinaBuySpreadsheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg',
                width: '512',
                height: '512',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://allchinabuyspreadsheet.bar/${article.slug}`,
            },
            inLanguage: 'en',
            isPartOf: {
              '@type': 'WebSite',
              '@id': 'https://allchinabuyspreadsheet.bar/#website',
              name: 'AllChinaBuySpreadsheet',
              url: 'https://allchinabuyspreadsheet.bar',
            },
          }),
        }}
      />
    </>
  );
}